/**
 * What this browser has already been counted for, and what it still owes (F-123).
 *
 * The whole of the deduplication rule, kept pure and DOM-free so it can be tested without a
 * renderer — `route.ts` and `chat/keyboard.ts` for the same split and the same reason. The
 * browser half that actually touches `localStorage` and the network is `report.ts`.
 *
 * ## What is stored, and what is deliberately not
 *
 * One key, holding a list of `stage:route` strings this browser has already been counted for.
 * **No identifier of any kind is created, stored or sent.** There is no visitor id, no
 * timestamp, no fingerprint and nothing that leaves the device except the two enum words
 * `stage` and `route` — which is why the server can honestly be told a count without ever
 * being able to say who it was about. Deduplication has to happen *somewhere*, and doing it
 * in the visitor's own browser is the version of it that cannot be used to follow anybody
 * around. The list is the only thing we would have to explain, and the explanation is "your
 * browser remembers it has already been counted, so it does not get counted twice".
 *
 * ## Once ever, not once per visit
 *
 * Every stage counts at most once per browser, for good. The question being answered is "of
 * the people who were offered this, how many took it" — a ratio of *people* — and a visitor
 * who comes back on Thursday is the same person who did not install on Tuesday. Counting per
 * visit would divide installs by page views and produce a number that falls whenever the
 * product gets more engaging, which is worse than having no number at all because it looks
 * like an answer.
 *
 * ## The one thing this cannot see
 *
 * **On iOS a home-screen app has its own storage jar**, separate from the Safari that
 * installed it. So the browser that was offered the install and the app that was installed
 * cannot recognise each other, no matter what either one writes down. That is the reason
 * `installed` is counted on a *first standalone launch* rather than attributed to a specific
 * earlier offer, and the reason the resulting figure is a cohort rate rather than a
 * per-person one. F-123 in docs/backlog.md says exactly what that rate can and cannot prove.
 */
import { FUNNEL_REPORTS, isFunnelReport, type FunnelRoute, type FunnelStage } from '@nabu/shared/install';
import type { InstallRoute } from './route.js';

/**
 * Where the list lives. Prefixed because the origin is shared with everything else the app
 * stores, and a bare `counted` is the kind of key two features fight over silently.
 */
export const COUNTED_KEY = 'nabu.install.counted';

/**
 * The offer routes, which are exactly the routes that put something on the screen.
 *
 * Returns `undefined` for `installed` and `none` rather than a default, because both of those
 * mean *nothing was offered* and a default would count an offer that was never made — padding
 * the denominator of the one ratio this whole feature exists to produce.
 */
export function offerRoute(route: InstallRoute): FunnelRoute | undefined {
  return route === 'prompt' || route === 'ios-safari' || route === 'ios-other' ? route : undefined;
}

/**
 * The list as it can actually come back, which is: anything at all.
 *
 * `localStorage` is a shared, writable, string-typed store that survives releases. What comes
 * out of it is last release's format, a half-written value from a tab that was killed
 * mid-write, a `null` from a private window, or something another script put there. Every one
 * of those has to mean "this browser has not been counted yet" — the cost of getting it wrong
 * is one duplicate count, and the cost of throwing is that the app fails to start, which is
 * not a trade worth making for a metric.
 *
 * Entries that are not in the shared vocabulary are dropped rather than kept. A stale entry
 * from a renamed stage would otherwise sit in the list for ever, and the list is capped
 * against it growing without bound.
 */
export function readCounted(raw: string | null | undefined): string[] {
  if (typeof raw !== 'string' || raw === '') return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const kept: string[] = [];
  for (const entry of parsed) {
    if (typeof entry !== 'string') continue;
    const [stage, route] = entry.split(':');
    if (!isFunnelReport(stage, route)) continue;
    if (kept.includes(entry)) continue;
    kept.push(entry);
  }
  // Cannot exceed the vocabulary, so a bug that appends in a loop cannot fill a visitor's
  // storage quota and take the app down with a QuotaExceededError on an unrelated write.
  return kept.slice(0, FUNNEL_REPORTS.length);
}

/** What `countOnce` decided: whether to send, and what to write back if we do. */
export interface CountDecision {
  /** Send the report. False means already counted, or not a pair we are allowed to count. */
  send: boolean;
  /**
   * The new value for `COUNTED_KEY`, present only when `send` is true.
   *
   * Written **before** the request rather than after it, which is the deliberate choice
   * between two ways of being wrong. Writing after a successful POST would re-send on every
   * page load until one succeeds, so a learner whose network is refusing us — or whose
   * browser blocks the request — generates one request per launch for ever. Writing first
   * loses a count when a request fails, and losing a count is a slightly low number, which is
   * the direction an honest funnel should err in anyway: it never overstates an install rate,
   * and overstating is the failure that would cost a native app.
   */
  store?: string;
}

/**
 * Decide whether this browser still owes us this count, and record that it no longer does.
 *
 * The unknown-pair case returns `send: false` rather than throwing, and it matters which:
 * this runs on a page load, and the alternative to silently declining to count something is
 * a white screen. The server refuses the same pair for the same reason from the other side —
 * see `isFunnelReport` — and logs it where an alarm can see it, which is where a mismatch
 * between these two lists is supposed to become visible.
 */
export function countOnce(
  raw: string | null | undefined,
  stage: FunnelStage,
  route: FunnelRoute,
): CountDecision {
  if (!isFunnelReport(stage, route)) return { send: false };
  const counted = readCounted(raw);
  const entry = `${stage}:${route}`;
  if (counted.includes(entry)) return { send: false };
  return { send: true, store: JSON.stringify([...counted, entry]) };
}
